import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Cpu, ShieldCheck, Boxes } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import ParticleField from "./ParticleField";
import { IMAGES, VIDEOS } from "@/i18n/assets";
import { toAbsolute } from "@/lib/api";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { t, lang, dir } = useLang();
  const { settings } = useSite();
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;
  const ref = useRef(null);

  // Overrides (per-lang for text; scalar for media)
  const badge = settings[`hero_badge_${lang}`] || t.hero.badge;
  const line1 = settings[`hero_title_line1_${lang}`] || t.hero.titleLines[0];
  const line2 = settings[`hero_title_line2_${lang}`] || t.hero.titleLines[1];
  const subtitle = settings[`hero_subtitle_${lang}`] || t.hero.subtitle;
  const videoUrl = toAbsolute(settings.hero_video_url) || VIDEOS.hero;
  const posterUrl = toAbsolute(settings.hero_poster_url) || VIDEOS.heroPoster;
  const stats = [0, 1, 2].map((i) => ({
    n: settings[`hero_stat${i + 1}_num`] || t.hero.stats[i].n,
    l: settings[`hero_stat${i + 1}_label_${lang}`] || t.hero.stats[i].l,
  }));

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const px = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 80, damping: 20 });
  const py = useSpring(useTransform(my, [-0.5, 0.5], [-18, 18]), { stiffness: 80, damping: 20 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen flex items-center overflow-hidden grain"
      data-testid="hero-section"
    >
      {/* Background video */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          key={videoUrl}
          src={videoUrl}
          poster={posterUrl}
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-video"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.82), rgba(5,9,20,0.9))" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,9,20,0.95), rgba(5,9,20,0.55))" }} />
      </div>
      <ParticleField className="opacity-70" />
      <div className="absolute inset-0 radial-fade" />
      <div className="absolute -top-40 start-1/4 h-96 w-96 rounded-full blur-[120px]" style={{ background: "rgba(0,242,96,0.12)" }} />

      <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 pt-28 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        {/* Text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-1.5 text-xs font-medium text-slate-300 mb-7"
            data-testid="hero-badge"
          >
            <Sparkles size={14} className="text-primary" />
            {badge}
          </motion.div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6">
            {[line1, line2].map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  style={i === 1 ? { color: "var(--primary)" } : {}}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed mb-9"
            data-testid="hero-subtitle"
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="flex flex-wrap items-center gap-4 mb-14"
          >
            <button
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-[#04120a] transition-transform hover:scale-105"
              style={{ background: "var(--primary)" }}
              data-testid="hero-start-btn"
            >
              {t.cta.start}
              <Arrow size={18} className="transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
            </button>
            <button
              onClick={() => scrollTo("services")}
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold border border-white/15 text-white hover:bg-white/5 transition-colors"
              data-testid="hero-quote-btn"
            >
              {t.cta.quote}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="flex flex-wrap gap-x-12 gap-y-6"
          >
            {stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="text-3xl sm:text-4xl font-extrabold text-primary glow-text">{s.n}</div>
                <div className="text-sm text-slate-400 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* 3D parallax scene */}
        <div className="lg:col-span-5 hidden lg:block" style={{ perspective: 1200 }}>
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="relative aspect-[4/5] w-full max-w-sm mx-auto"
          >
            {/* main image card */}
            <motion.div style={{ x: px, y: py }} className="absolute inset-0 rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img src={IMAGES.heroCity} alt="Smart city" className="h-full w-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.1), rgba(5,9,20,0.85))" }} />
            </motion.div>

            {/* floating glass chips */}
            <motion.div
              style={{ transform: "translateZ(60px)", x: useTransform(px, (v) => v * 1.6), y: useTransform(py, (v) => v * 1.6) }}
              className="absolute -start-6 top-16 glass rounded-2xl border border-white/10 px-4 py-3 flex items-center gap-3"
            >
              <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "rgba(0,242,96,0.15)" }}>
                <Cpu size={18} className="text-primary" />
              </div>
              <span className="text-sm font-semibold">AI</span>
            </motion.div>

            <motion.div
              style={{ transform: "translateZ(90px)", x: useTransform(px, (v) => v * 2), y: useTransform(py, (v) => v * 2) }}
              className="absolute -end-4 top-40 glass rounded-2xl border border-white/10 px-4 py-3 flex items-center gap-3"
            >
              <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "rgba(0,242,96,0.15)" }}>
                <ShieldCheck size={18} className="text-primary" />
              </div>
              <span className="text-sm font-semibold">SCADA</span>
            </motion.div>

            <motion.div
              style={{ transform: "translateZ(70px)", x: useTransform(px, (v) => v * 1.4), y: useTransform(py, (v) => v * 1.4) }}
              className="absolute -start-4 bottom-10 glass rounded-2xl border border-white/10 px-4 py-3 flex items-center gap-3"
            >
              <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "rgba(0,242,96,0.15)" }}>
                <Boxes size={18} className="text-primary" />
              </div>
              <span className="text-sm font-semibold">ERP</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* scroll hint */}
      <div className="absolute bottom-6 inset-x-0 flex justify-center z-10">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="h-10 w-6 rounded-full border-2 border-white/20 flex items-start justify-center p-1.5">
          <div className="h-2 w-1 rounded-full" style={{ background: "var(--primary)" }} />
        </motion.div>
      </div>
    </section>
  );
}
